import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bed, Compass, MapPin } from 'lucide-react';
import { ACCOMMODATIONS, TASTING_STOPS } from '../lib/data';
import { getStateHref } from '../lib/trailHubs';
import { bookingSearchUrl } from '../utils/booking';
import NotFoundPage from './NotFoundPage';

interface GuidePageConfig {
  slug: string;
  eyebrow: string;
  title: string;
  metaDescription: string;
  intro: string;
  sections: Array<{
    title: string;
    body: string;
    bullets?: string[];
  }>;
  stopIds: string[];
  stayIds: string[];
  related: Array<{ label: string; href: string }>;
}

const GUIDE_PAGES: GuidePageConfig[] = [
  {
    slug: 'olive-oil-tasting-dripping-springs',
    eyebrow: 'Texas Hill Country tasting stop',
    title: 'Olive Oil Tasting in Dripping Springs, Texas',
    metaDescription:
      'Plan an olive oil tasting stop in Dripping Springs, Texas, with visitor notes, nearby stay bases, and Southwest olive-route context.',
    intro:
      'Use this page to plan olive oil tasting in Dripping Springs: what the main tasting stops offer, what to confirm before you go, how long to allow, and which nearby bases make sense for a weekend.',
    stopIds: ['texas-hill-country-olive-co', 'camp-lucy-olive-oil-tasting'],
    stayIds: ['camp-lucy', 'lucky-arrow-retreat', 'alexander-at-creek-road'],
    sections: [
      {
        title: 'Why Dripping Springs works',
        body:
          'The route has a clear anchor here: Texas Hill Country Olive Co. gives travelers guided tasting flights, orchard context, a retail shop, and a bistro in one stop. Camp Lucy adds a more hospitality-led tasting class nearby, which makes the town useful for both first-time tasters and weekend travelers.',
      },
      {
        title: 'What to expect from the tasting',
        body:
          'Expect tasting-room guidance, oil and balsamic comparisons, pantry shopping, and a short learning curve around fruitiness, bitterness, and peppery finish. Treat current hours, tour slots, tasting prices, and class availability as live details to confirm before driving out.',
        bullets: [
          'Use Dripping Springs as a half-day tasting stop if you are already in Austin or the Hill Country.',
          'Make it a full weekend when you want lodging, dinner, and a slower food-trip pace.',
          'Confirm official hours and reservation requirements directly with the venue before you go.',
        ],
      },
      {
        title: 'Where to stay',
        body:
          'Dripping Springs is the most natural base if the olive stop is the reason for the trip. Austin can work when you want more restaurants and nightlife, but it turns the tasting into a drive-out excursion rather than the center of the weekend.',
      },
    ],
    related: [
      { label: 'Southwest olive mill tours', href: '/olive-mill-tours-southwest/' },
      { label: 'Texas olive oil tasting guide', href: '/texas-olive-oil-tasting/' },
      { label: 'Texas chapter', href: getStateHref('TX') },
    ],
  },
  {
    slug: 'olive-mill-tours-southwest',
    eyebrow: 'Tours, tastings, and mill visits',
    title: 'Olive Mill Tours in the Southwest',
    metaDescription:
      'Compare Southwest olive mill tours, tasting rooms, orchard stops, and weekend stay bases in Texas, Arizona, and New Mexico.',
    intro:
      'A Southwest olive trip can mean a working mill tour, an orchard tasting, a retail sampling room, or a resort class. This guide keeps the scope grounded in real anchors: Texas Hill Country and Dripping Springs, Arizona and Queen Creek, plus New Mexico tasting-room stops where the guide has substance.',
    stopIds: ['texas-hill-country-olive-co', 'central-texas-olive-ranch', 'queen-creek-olive-mill', 'santa-fe-olive-oil-balsamic'],
    stayIds: ['camp-lucy', 'la-fonda-on-the-plaza', 'the-scott-resort'],
    sections: [
      {
        title: 'What counts as an olive mill tour',
        body:
          'A true mill visit usually gives travelers production context: groves, harvest timing, pressing, milling equipment, or a class built around how olive oil is made. Retail tasting rooms can still be worthwhile, but they should be planned as shorter sampling stops rather than full mill tours.',
      },
      {
        title: 'Texas, Arizona, and New Mexico',
        body:
          'Texas gives the route its strongest Hill Country tasting chapter, Arizona has the clearest working-mill anchor in Queen Creek, and New Mexico is better treated as an urban tasting-room chapter folded into Santa Fe or Albuquerque food travel.',
        bullets: [
          'Texas: start with Dripping Springs and the broader Hill Country tasting corridor.',
          'Arizona: build around Queen Creek first, then use Phoenix or Scottsdale for the overnight.',
          'New Mexico: keep expectations honest and plan for tasting rooms, pantry shopping, and walkable food stops.',
        ],
      },
      {
        title: 'Planning around changing access',
        body:
          'Tour schedules, harvest-season programming, classes, and tasting-room hours can change. Use this page as a starting point, then confirm directly with each stop before making the olive visit the anchor of a trip.',
      },
    ],
    related: [
      { label: 'Dripping Springs tasting guide', href: '/olive-oil-tasting-dripping-springs/' },
      { label: 'Arizona olive oil tasting guide', href: '/arizona-olive-oil-tasting/' },
      { label: 'Route map', href: '/' },
    ],
  },
  {
    slug: 'texas-olive-oil-tasting',
    eyebrow: 'State tasting guide',
    title: 'Texas Olive Oil Tasting Guide',
    metaDescription:
      'Plan Texas olive oil tasting stops around Dripping Springs, Gruene, and Central Texas orchard detours.',
    intro:
      'Texas is the backbone of the current route because it combines public-facing orchard stops, resort tasting classes, river-town sampling, and enough stay options to make the drive feel like a real weekend.',
    stopIds: [
      'texas-hill-country-olive-co',
      'camp-lucy-olive-oil-tasting',
      'gruene-olive-tasting-room',
      'central-texas-olive-ranch',
      'dells-favorite-texas-olive-ranch',
    ],
    stayIds: ['camp-lucy', 'lucky-arrow-retreat', 'gruene-river-hotel'],
    sections: [
      {
        title: 'Start in Hill Country',
        body:
          'For most travelers, Dripping Springs is the cleanest first chapter. It has the strongest public tasting anchor and nearby lodging that can turn the olive stop into a food-focused getaway.',
      },
      {
        title: 'Add Gruene or Central Texas only when it helps the trip',
        body:
          'Gruene works as a casual river-town tasting add-on. Georgetown and Elgin are better as producer detours for travelers who want more orchard context and are comfortable confirming access before driving out.',
      },
      {
        title: 'Pair tasting with the rest of the weekend',
        body:
          'Texas olive tasting pairs naturally with wineries, distilleries, barbecue, river-town browsing, and Hill Country lodging. Keep the route selective so the day still feels like a weekend, not a checklist.',
      },
    ],
    related: [
      { label: 'Dripping Springs tasting guide', href: '/olive-oil-tasting-dripping-springs/' },
      { label: 'Texas chapter', href: getStateHref('TX') },
      { label: 'Southwest olive mill tours', href: '/olive-mill-tours-southwest/' },
    ],
  },
  {
    slug: 'arizona-olive-oil-tasting',
    eyebrow: 'State tasting guide',
    title: 'Arizona Olive Oil Tasting Guide',
    metaDescription:
      'Plan Arizona olive oil tasting around Queen Creek Olive Mill, Phoenix-area tasting stops, and Scottsdale stay bases.',
    intro:
      'Arizona is the desert chapter of the route. Queen Creek gives the state its strongest olive anchor, while Phoenix and Scottsdale make the logistics easier for travelers planning an overnight.',
    stopIds: ['queen-creek-olive-mill', 'sonoran-desert-olive-oil-company'],
    stayIds: ['the-scott-resort', 'hotel-adeline'],
    sections: [
      {
        title: 'Build around Queen Creek first',
        body:
          "Queen Creek Olive Mill is the state's most substantial olive stop in the current guide, with a public mill-centered visitor experience, classes, marketplace browsing, and food options on site.",
      },
      {
        title: 'Use Phoenix and Scottsdale as the stay base',
        body:
          'The metro area gives travelers stronger hotel and dinner options than trying to sleep beside every stop. Plan Queen Creek as the anchor visit, then use Scottsdale or Phoenix for the evening.',
      },
      {
        title: 'Know the difference between a mill visit and a tasting room',
        body:
          'Arizona can be a full olive stop or a lighter tasting-room browse depending on the day. Confirm class times, current hours, and event schedules before making the drive.',
      },
    ],
    related: [
      { label: 'Southwest olive mill tours', href: '/olive-mill-tours-southwest/' },
      { label: 'Arizona chapter', href: getStateHref('AZ') },
      { label: 'Route map', href: '/' },
    ],
  },
  {
    slug: 'new-mexico-olive-oil-tasting',
    eyebrow: 'State tasting guide',
    title: 'New Mexico Olive Oil Tasting Guide',
    metaDescription:
      'Plan New Mexico olive oil tasting around Santa Fe, Albuquerque, tasting rooms, food shopping, and walkable stay bases.',
    intro:
      'New Mexico is the food-trip chapter of the Southwest olive guide: Santa Fe and Albuquerque tasting rooms, pantry shopping, Plaza walks, and hotel bases that make the olive stop part of a larger weekend rather than a fake orchard loop.',
    stopIds: ['santa-fe-olive-oil-balsamic', 'abq-olive-oil-company'],
    stayIds: ['la-fonda-on-the-plaza', 'la-posada-de-santa-fe'],
    sections: [
      {
        title: 'Start with Santa Fe if you want the strongest trip feel',
        body:
          'Santa Fe is the easier first New Mexico chapter because the olive stop fits naturally into a walkable food-and-art weekend: tasting room, Plaza browsing, dinner, and a stay base close enough to keep the day simple.',
      },
      {
        title: 'Use Albuquerque and Corrales for a practical tasting stop',
        body:
          'The Albuquerque-area stop works better as a lower-friction tasting-room visit than as a full destination mill tour. Treat it as a useful road-trip or city-weekend add-on, and confirm current store hours or class availability before going.',
      },
      {
        title: 'Do not plan New Mexico like a mill-tour loop',
        body:
          'This state belongs in the guide because it has real olive-oil tasting-room substance, not because it has the same orchard density as Texas or the same mill anchor as Arizona. Keep the route compact and food-first.',
      },
    ],
    related: [
      { label: 'Southwest olive mill tours', href: '/olive-mill-tours-southwest/' },
      { label: 'New Mexico chapter', href: getStateHref('NM') },
      { label: 'Route map', href: '/' },
    ],
  },
];

export function getGuidePageBySlug(slug: string): GuidePageConfig | undefined {
  return GUIDE_PAGES.find((page) => page.slug === slug);
}

export const GUIDE_PAGE_SLUGS = GUIDE_PAGES.map((page) => page.slug);

export default function GuidePage({ slug }: { slug: string }) {
  const page = getGuidePageBySlug(slug);

  useEffect(() => {
    if (!page) return;
    const previousTitle = document.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.getAttribute('content');
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const previousCanonical = canonical?.getAttribute('href');

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }

    document.title = `${page.title} | Olive Oil Trails`;
    description?.setAttribute('content', page.metaDescription);
    canonical.setAttribute('href', `https://oliveoiltrails.com/${page.slug}/`);

    return () => {
      document.title = previousTitle;
      if (previousDescription) description?.setAttribute('content', previousDescription);
      if (previousCanonical) canonical?.setAttribute('href', previousCanonical);
    };
  }, [page]);

  if (!page) {
    return <NotFoundPage />;
  }

  const stops = page.stopIds
    .map((id) => TASTING_STOPS.find((stop) => stop.id === id))
    .filter((stop): stop is NonNullable<typeof stop> => Boolean(stop));
  const stays = page.stayIds
    .map((id) => ACCOMMODATIONS.find((stay) => stay.id === id))
    .filter((stay): stay is NonNullable<typeof stay> => Boolean(stay));

  return (
    <div className="min-h-screen bg-bg text-ink">
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center text-white">
              <Compass size={16} />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg">Olive Oil Trails</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-light">Southwest tasting weekends</div>
            </div>
          </Link>
          <Link to="/" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink-light hover:text-ink transition-colors">
            <ArrowLeft size={14} /> Back to route
          </Link>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-14 md:py-20 space-y-14">
        <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
          <div className="space-y-6">
            <span className="pill-minimal active">{page.eyebrow}</span>
            <h1 className="text-5xl md:text-7xl font-serif tracking-tight leading-[1.02]">
              {page.title}
            </h1>
            <p className="text-lg md:text-xl text-ink-light leading-relaxed max-w-4xl">
              {page.intro}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/olive-oil-tasting-dripping-springs/" className="btn-minimal">
                Dripping Springs
              </Link>
              <Link to="/olive-mill-tours-southwest/" className="btn-minimal">
                Mill tours
              </Link>
              <Link to="/plan" className="btn-minimal">
                Build a route
              </Link>
            </div>
          </div>

          <aside className="bg-white/80 border border-border rounded-[32px] p-6 md:p-8 space-y-5">
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-accent">
              <MapPin size={13} /> Before you go
            </span>
            <p className="text-sm md:text-base text-ink-light leading-relaxed">
              Hours, tasting fees, class schedules, tour availability, and seasonal access can change.
              Use this guide as a starting point, then confirm directly with the official venue before driving out.
            </p>
            <div className="grid gap-3">
              <Link to="/stops" className="btn-minimal text-center">
                Browse all stops
              </Link>
              <Link to="/plan" className="btn-minimal text-center">
                Build a route
              </Link>
            </div>
          </aside>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {page.sections.map((section) => (
            <article key={section.title} className="bg-white/80 border border-border rounded-[28px] p-6 space-y-4">
              <h2 className="text-3xl font-serif tracking-tight">{section.title}</h2>
              <p className="text-sm text-ink-light leading-relaxed">{section.body}</p>
              {section.bullets ? (
                <div className="grid gap-2 pt-1">
                  {section.bullets.map((bullet) => (
                    <p key={bullet} className="text-sm text-ink-light leading-relaxed rounded-2xl bg-sidebar/60 border border-border p-3">
                      {bullet}
                    </p>
                  ))}
                </div>
              ) : null}
            </article>
          ))}
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-[1fr_0.85fr] gap-8 items-start">
          <div className="space-y-5">
            <div className="space-y-2">
              <span className="pill-minimal active">Featured tasting stops</span>
              <h2 className="text-4xl md:text-5xl font-serif tracking-tight">Stops that fit this page.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {stops.map((stop) => (
                <article key={stop.id} className="card-minimal p-6 bg-white/80 space-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-accent font-bold italic">
                      {stop.city}, {stop.state}
                    </p>
                    <h3 className="text-2xl font-serif tracking-tight">{stop.name}</h3>
                  </div>
                  <p className="text-sm text-ink-light leading-relaxed">{stop.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {stop.experiences.map((experience) => (
                      <span key={experience} className="pill-minimal">{experience}</span>
                    ))}
                  </div>
                  <Link to="/stops" className="btn-minimal inline-flex">
                    View in route planner
                  </Link>
                </article>
              ))}
            </div>
          </div>

          <aside className="bg-sidebar/60 border border-border rounded-[32px] p-6 md:p-8 space-y-5">
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-accent">
              <Bed size={13} /> Weekend stay planning
            </span>
            <h2 className="text-3xl md:text-4xl font-serif tracking-tight">Stay near the route, not over the whole map.</h2>
            <p className="text-sm text-ink-light leading-relaxed">
              If this becomes a weekend trip, choose a base that keeps the tasting stop easy to reach and still leaves room for dinner, shopping, and slow travel.
            </p>
            <div className="grid gap-3">
              {stays.map((stay) => (
                <a
                  key={stay.id}
                  href={bookingSearchUrl(stay)}
                  target="_blank"
                  rel="noreferrer noopener sponsored"
                  data-stay22="book"
                  className="rounded-2xl bg-white border border-border p-4 hover:border-accent transition-colors"
                >
                  <p className="text-[10px] uppercase tracking-widest text-accent font-bold">{stay.city}, {stay.state}</p>
                  <p className="font-serif text-xl tracking-tight">{stay.name}</p>
                  <p className="text-xs text-ink-light mt-1">{stay.style} · {stay.priceRange}</p>
                </a>
              ))}
            </div>
          </aside>
        </section>

        <section className="bg-white/80 border border-border rounded-[32px] p-6 md:p-8 space-y-5">
          <span className="pill-minimal active">Related planning pages</span>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {page.related.map((item) => (
              <Link key={item.href} to={item.href} className="rounded-[24px] bg-sidebar/60 border border-border p-5 hover:border-accent hover:bg-white transition-colors">
                <p className="text-2xl font-serif tracking-tight">{item.label}</p>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
